import StringMultiDropdownFilter from 'core/elements/grid/filters/StringMultiDropdownFilter'

export enum NodeType {
  Worker = 'Worker',
  ControlPlane = 'Control Plane',
  GPU = 'GPU',
}

export enum K8sNodeStatus {
  Ready = 'Ready',
  NotReady = 'NotReady',
  MemoryPressure = 'MemoryPressure',
  DiskPressure = 'DiskPressure',
  PIDPressure = 'PIDPressure',
  NetworkUnavailable = 'NetworkUnavailable',
  Unknown = 'Unknown',
}

export enum VMPhase {
  Running = 'Running',
  Provisioning = 'Provisioning',
  Provisioned = 'Provisioned',
  Failed = 'Failed',
  ScalingUp = 'ScalingUp',
  ScalingDown = 'ScalingDown',
  Deleting = 'Deleting',
  Ready = 'Ready',
  Pending = 'Pending',
  Unknown = 'Unknown',
}

export enum GpuEnabled {
  Yes = 'yes',
  No = 'no',
}

const anyOpt = { label: 'All', value: 'any' }

const toOptions = (values: (string | undefined)[]) => {
  const unique = [...new Set(values.filter(Boolean))] as string[]
  return [anyOpt, ...unique.map((v) => ({ label: v, value: v }))]
}

// FR-008: empty selection or 'any' selected → pass all rows
const anyOrMatch = (fn: (item: any, value: string[]) => boolean) => (item: any, value: string[]) =>
  !value?.length || value.includes('any') || fn(item, value)

export function buildWorkerNodeFilters(machines: any[], isBYOHCluster: boolean) {
  const filters: any[] = [
    // FR-001: Node Type
    {
      key: 'nodeType',
      label: 'Node Type',
      FilterComponent: StringMultiDropdownFilter,
      filterComponentProps: { label: 'Node Type' },
      filterComponentOptionsPropName: 'dropdownOptions',
      getOptionsFn: () => [
        anyOpt,
        { label: NodeType.Worker, value: NodeType.Worker },
        { label: NodeType.ControlPlane, value: NodeType.ControlPlane },
        { label: NodeType.GPU, value: NodeType.GPU },
      ],
      equalityComparerFn: anyOrMatch((item, value) => value.includes(item.nodeType)),
    },
    // FR-002: Node Group (dynamic)
    {
      key: 'nodeGroup',
      label: 'Node Group',
      FilterComponent: StringMultiDropdownFilter,
      filterComponentProps: { label: 'Node Group' },
      filterComponentOptionsPropName: 'dropdownOptions',
      getOptionsFn: () => toOptions(machines.map((m) => m.machineDeployment?.metadata?.name)),
      equalityComparerFn: anyOrMatch((item, value) =>
        value.includes(item.machineDeployment?.metadata?.name),
      ),
    },
    // FR-003: GPU Enabled
    {
      key: 'gpuEnabled',
      label: 'GPU Enabled',
      FilterComponent: StringMultiDropdownFilter,
      filterComponentProps: { label: 'GPU Enabled' },
      filterComponentOptionsPropName: 'dropdownOptions',
      getOptionsFn: () => [
        anyOpt,
        { label: 'Yes', value: GpuEnabled.Yes },
        { label: 'No', value: GpuEnabled.No },
      ],
      equalityComparerFn: anyOrMatch((item, value) =>
        value.includes(item.gpu?.enabled ? GpuEnabled.Yes : GpuEnabled.No),
      ),
    },
    // FR-004: Node Status (k8s condition)
    {
      key: 'nodeStatus',
      label: 'Node Status',
      FilterComponent: StringMultiDropdownFilter,
      filterComponentProps: { label: 'Node Status' },
      filterComponentOptionsPropName: 'dropdownOptions',
      getOptionsFn: () => [
        anyOpt,
        { label: K8sNodeStatus.Ready, value: K8sNodeStatus.Ready },
        { label: K8sNodeStatus.MemoryPressure, value: K8sNodeStatus.MemoryPressure },
        { label: K8sNodeStatus.DiskPressure, value: K8sNodeStatus.DiskPressure },
        { label: K8sNodeStatus.PIDPressure, value: K8sNodeStatus.PIDPressure },
        { label: K8sNodeStatus.NetworkUnavailable, value: K8sNodeStatus.NetworkUnavailable },
        { label: K8sNodeStatus.NotReady, value: K8sNodeStatus.NotReady },
        { label: K8sNodeStatus.Unknown, value: K8sNodeStatus.Unknown },
      ],
      equalityComparerFn: anyOrMatch((item, value) => value.includes(item.k8sNodeStatusType)),
    },
    // FR-005: VM Status (machine phase)
    {
      key: 'vmStatus',
      label: 'VM Status',
      FilterComponent: StringMultiDropdownFilter,
      filterComponentProps: { label: 'VM Status' },
      filterComponentOptionsPropName: 'dropdownOptions',
      getOptionsFn: () => [
        anyOpt,
        { label: VMPhase.Running, value: VMPhase.Running },
        { label: VMPhase.Provisioning, value: VMPhase.Provisioning },
        { label: VMPhase.Provisioned, value: VMPhase.Provisioned },
        { label: VMPhase.Failed, value: VMPhase.Failed },
        { label: VMPhase.ScalingUp, value: VMPhase.ScalingUp },
        { label: VMPhase.ScalingDown, value: VMPhase.ScalingDown },
        { label: VMPhase.Deleting, value: VMPhase.Deleting },
        { label: VMPhase.Ready, value: VMPhase.Ready },
        { label: VMPhase.Pending, value: VMPhase.Pending },
        { label: VMPhase.Unknown, value: VMPhase.Unknown },
      ],
      equalityComparerFn: anyOrMatch((item, value) => value.includes(item.phase)),
    },
    // FR-007: K8s Version (dynamic)
    {
      key: 'k8sVersion',
      label: 'K8s Version',
      FilterComponent: StringMultiDropdownFilter,
      filterComponentProps: { label: 'K8s Version' },
      filterComponentOptionsPropName: 'dropdownOptions',
      getOptionsFn: () => toOptions(machines.map((m) => m.spec?.version)),
      equalityComparerFn: anyOrMatch((item, value) => value.includes(item.spec?.version)),
    },
  ]

  // FR-006: Flavor hidden for BYOH clusters
  if (!isBYOHCluster) {
    filters.splice(5, 0, {
      key: 'flavor',
      label: 'Flavor',
      FilterComponent: StringMultiDropdownFilter,
      filterComponentProps: { label: 'Flavor' },
      filterComponentOptionsPropName: 'dropdownOptions',
      getOptionsFn: () => toOptions(machines.map((m) => m.machineFlavor)),
      equalityComparerFn: anyOrMatch((item, value) => value.includes(item.machineFlavor)),
    })
  }

  return filters
}
